import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Person24Regular,
  Mail24Regular,
  Call24Regular,
  Shield24Regular,
  Building24Regular,
  LockClosed24Regular,
  Eye20Regular,
  EyeOff20Regular,
  ArrowSync20Regular,
  Checkmark16Filled,
  Dismiss20Regular,
  Save20Regular,
  Warning20Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  calculatePasswordStrength,
  generatePassword,
} from "../../users/passwordUtils";
import { ApiError, getUserById, updateUser } from "../../users/usersApi";
import { useAuth } from "../../auth/AuthContext";
import { accessControlClient } from "../../acessos/acessosClient";
import { APP_LOCALE } from "../../config/locale";

type PerfilAcesso = "Administrador" | "Utilizador" | "Equipa interna";
type EstadoUtilizador = "Ativo" | "Inativo" | "Pendente";

const mapRole = (perfil: PerfilAcesso): string => {
  if (perfil === "Administrador") return "Administrator";
  if (perfil === "Equipa interna") return "TeamMember";
  return "User";
};

const mapPerfil = (roles: string[]): PerfilAcesso => {
  if (roles.some((role) => role === "Administrator")) return "Administrador";
  if (roles.some((role) => role === "TeamMember")) return "Equipa interna";
  return "Utilizador";
};

const mapEstado = (
  emailConfirmed: boolean | undefined,
  lockoutEnd?: string | null,
): EstadoUtilizador => {
  if (lockoutEnd) {
    const parsed = new Date(lockoutEnd);
    if (!Number.isNaN(parsed.getTime()) && parsed > new Date()) {
      return "Inativo";
    }
  }

  if (emailConfirmed === false) {
    return "Pendente";
  }

  return "Ativo";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar utilizadores.";
    }
    if (error.status === 400 && error.message) {
      try {
        const parsed = JSON.parse(error.message) as {
          errors?: Record<string, string[]>;
          detail?: string;
          title?: string;
        };
        if (parsed?.errors) {
          const messages = Object.values(parsed.errors).flat();
          if (messages.length > 0) {
            return messages.join(" ");
          }
        }
        if (parsed?.detail) {
          return parsed.detail;
        }
        if (parsed?.title) {
          return parsed.title;
        }
      } catch {
        // Ignore invalid JSON.
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o utilizador.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar utilizadores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o utilizador.";
};

export function EditarUtilizadorView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userId } = useParams();
  const [nome, setNome] = useState("");
  const [apelido, setApelido] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [perfil, setPerfil] = useState<PerfilAcesso>("Utilizador");
  // Cargos adicionais (os criados na Gestão de Acessos): um utilizador pode ter vários — o
  // acesso é a união do perfil base com estes.
  const [extraRoles, setExtraRoles] = useState<string[]>([]);
  const [customRoles, setCustomRoles] = useState<string[]>([]);
  const [departamento, setDepartamento] = useState("");
  const [estado, setEstado] = useState<EstadoUtilizador>("Ativo");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<
    string | null
  >(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const hasAssociatedData = true;
  const isAdmin = user?.roles?.includes("Administrator") ?? false;

  useEffect(() => {
    let active = true;
    accessControlClient
      .getAccessControl()
      .then((vm) => {
        if (!active) return;
        setCustomRoles(
          (vm.roles ?? [])
            .filter((role) => !role.isSystem)
            .map((role) => role.name ?? "")
            .filter((name) => name !== ""),
        );
      })
      .catch(() => {
        // Sem a lista, o formulário funciona só com o perfil base.
      });
    return () => {
      active = false;
    };
  }, []);
  const isReadOnly = isLoading || !isAdmin;

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      if (!userId) {
        setLoadError("Utilizador inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getUserById(userId);
        if (!isMounted) return;

        setNome(response.firstName ?? "");
        setApelido(response.lastName ?? "");
        setEmail(response.email ?? "");
        setTelefone(response.phoneNumber ?? "");
        setDepartamento(response.department ?? "");
        setPerfil(mapPerfil(response.roles ?? []));
        setExtraRoles(
          (response.roles ?? []).filter(
            (role) => !["Administrator", "User", "TeamMember"].includes(role),
          ),
        );
        setEstado(mapEstado(response.emailConfirmed, response.lockoutEnd));
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const passwordStrength = useMemo(
    () => (password ? calculatePasswordStrength(password) : null),
    [password],
  );

  const passwordRequirements = useMemo(
    () => ({
      minLength: password.length >= 6,
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    }),
    [password],
  );

  const passwordMeetsPolicy = Object.values(
    passwordRequirements,
  ).every(Boolean);

  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const canSubmit =
    isAdmin &&
    !isLoading &&
    !isSaving &&
    nome.trim().length > 0 &&
    apelido.trim().length > 0 &&
    email.trim().length > 0;

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    setPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!userId) {
      setSubmitError("Utilizador inválido.");
      return;
    }

    if (!isAdmin) {
      setSubmitError("Sem permissões para editar utilizadores.");
      return;
    }

    if (resetPassword) {
      if (!passwordMeetsPolicy) {
        setSubmitError(
          "A password deve ter pelo menos 6 caracteres e incluir um caracter especial.",
        );
        return;
      }

      if (password !== confirmPassword) {
        setSubmitError("As passwords não coincidem.");
        return;
      }
    }

    setIsSaving(true);

    try {
      await updateUser(userId, {
        email: email.trim(),
        firstName: nome.trim(),
        lastName: apelido.trim(),
        phoneNumber: telefone.trim() || null,
        department: departamento.trim() || null,
        role: mapRole(perfil),
        roles: [mapRole(perfil), ...extraRoles],
        isActive: estado !== "Inativo",
        newPassword: resetPassword ? password : undefined,
      });

      navigate("/utilizadores", {
        state: { toast: "Utilizador atualizado com sucesso" },
      });
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setSubmitError(
      "A eliminação de utilizadores ainda não está disponível.",
    );
  };

  const handleMarkInactive = () => {
    if (!isAdmin) {
      setSubmitError("Sem permissões para editar utilizadores.");
      return;
    }

    setEstado("Inativo");
    const form = document.getElementById(
      "editar-utilizador-form",
    ) as HTMLFormElement | null;
    form?.requestSubmit();
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/utilizadores")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-utilizador-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={!canSubmit}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Utilizador"
        subtitle="Atualize os dados do utilizador e permissões de acesso."
        actions={actions}
        backButton={{ onClick: () => navigate("/utilizadores") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {!isAdmin && (
            <div
              className="bg-card border border-border px-4 py-3 mb-6 text-secondary-text"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              Sem permissões para editar utilizadores.
            </div>
          )}

          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-utilizador-form" onSubmit={handleSubmit}>
            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Person24Regular />
                Informação Básica
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nome"
                    className="block text-card-foreground mb-2"
                  >
                    Nome{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <input
                    id="nome"
                    type="text"
                    value={nome}
                    onChange={(event) =>
                      setNome(event.target.value)
                    }
                    required
                    placeholder="Ex: Maria"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <label
                    htmlFor="apelido"
                    className="block text-card-foreground mb-2"
                  >
                    Apelido{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <input
                    id="apelido"
                    type="text"
                    value={apelido}
                    onChange={(event) =>
                      setApelido(event.target.value)
                    }
                    required
                    placeholder="Ex: Silva"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-card-foreground mb-2"
                  >
                    Email{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <div className="relative">
                    <Mail24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      required
                      placeholder="maria.silva@aja2.pt"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-card-foreground mb-2"
                  >
                    Telefone
                  </label>
                  <div className="relative">
                    <Call24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                    <input
                      id="telefone"
                      type="tel"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      placeholder="+351 210 000 000"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Shield24Regular />
                Permissões e Acesso
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="perfil"
                    className="block text-card-foreground mb-2"
                  >
                    Perfil de acesso{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <select
                    id="perfil"
                    value={perfil}
                    onChange={(event) =>
                      setPerfil(event.target.value as PerfilAcesso)
                    }
                    required
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={isReadOnly}
                  >
                    <option value="Utilizador">Utilizador</option>
                    <option value="Administrador">Administrador</option>
                    <option value="Equipa interna">Equipa interna</option>
                  </select>
                  {customRoles.length > 0 && (
                    <div
                      className="mt-3 border border-[var(--border)] p-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <span id="extra-roles-label" className="block text-card-foreground text-sm mb-1" style={{ fontWeight: "var(--font-weight-medium)" }}>
                        Cargos adicionais
                      </span>
                      <p className="text-xs text-[var(--muted-foreground)] mb-2">
                        Somam-se ao perfil acima. Geridos na Gestão de Acessos.
                      </p>
                      <div role="group" aria-labelledby="extra-roles-label" className="flex flex-col gap-1">
                        {customRoles.map((roleName) => (
                          <label key={roleName} className="flex items-center gap-2 text-sm text-card-foreground py-1" style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={extraRoles.includes(roleName)}
                              disabled={isReadOnly}
                              onChange={() =>
                                setExtraRoles((previous) =>
                                  previous.includes(roleName)
                                    ? previous.filter((name) => name !== roleName)
                                    : [...previous, roleName],
                                )
                              }
                            />
                            {roleName}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    {perfil === "Administrador" &&
                      "Vê e gere tudo. Nenhum cargo ou funcionalidade o limita."}
                    {perfil === "Utilizador" &&
                      "Acede às funcionalidades do cargo «Utilizador» (definidas na Gestão de Acessos)."}
                    {perfil === "Equipa interna" &&
                      "Só a Folha de Horas e o próprio perfil (trabalhadores e encarregados de obra)."}
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="departamento"
                    className="block text-card-foreground mb-2"
                  >
                    Departamento
                  </label>
                  <div className="relative">
                    <Building24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                    <input
                      id="departamento"
                      type="text"
                      value={departamento}
                      onChange={(event) =>
                        setDepartamento(event.target.value)
                      }
                      placeholder="Ex: Obras, Comercial, Direção"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-card-foreground mb-2">
                    Estado
                  </label>
                  <div
                    className="w-full h-10 px-3 bg-[var(--accent)] border border-[var(--border)] flex items-center gap-2"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {estado === "Ativo" && (
                      <>
                        <div className="w-2 h-2 rounded-full bg-[var(--success)]" />
                        <span className="text-card-foreground">Ativo</span>
                        <span className="text-xs text-[var(--muted-foreground)] ml-auto">
                          Utilizador pode aceder ao sistema
                        </span>
                      </>
                    )}
                    {estado === "Inativo" && (
                      <>
                        <div className="w-2 h-2 rounded-full bg-[var(--muted-foreground)]" />
                        <span className="text-card-foreground">Inativo</span>
                        <span className="text-xs text-[var(--muted-foreground)] ml-auto">
                          Utilizador não pode aceder ao sistema
                        </span>
                      </>
                    )}
                    {estado === "Pendente" && (
                      <>
                        <div className="w-2 h-2 rounded-full bg-[var(--warning)]" />
                        <span className="text-card-foreground">Pendente</span>
                        <span className="text-xs text-[var(--muted-foreground)] ml-auto">
                          Aguarda ativação da conta
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    Para alterar o estado do utilizador, utilize a
                    zona de perigo abaixo
                  </p>
                </div>
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-4 flex items-center gap-2">
                <LockClosed24Regular />
                Redefinir Password
              </h2>

              {!resetPassword ? (
                <div>
                  <p className="text-sm text-[var(--muted-foreground)] mb-4">
                    Crie uma nova password para este utilizador. A
                    password antiga será substituída.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setResetPassword(true);
                      setPassword("");
                      setConfirmPassword("");
                      setShowPassword(false);
                      setShowConfirmPassword(false);
                      setSubmitError(null);
                    }}
                    className="h-10 px-4 bg-[var(--accent)] border border-[var(--border)] text-card-foreground hover:bg-[var(--accent)]/80 transition-all flex items-center gap-2"
                    style={{ borderRadius: "var(--radius-button)" }}
                    disabled={isReadOnly}
                  >
                    <LockClosed24Regular className="w-5 h-5" />
                    Redefinir password
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="h-10 px-4 bg-[var(--accent)] border border-[var(--border)] text-card-foreground hover:bg-[var(--accent)]/80 transition-all flex items-center gap-2"
                      style={{ borderRadius: "var(--radius-button)" }}
                      disabled={isReadOnly}
                    >
                      <ArrowSync20Regular className="w-5 h-5" />
                      Gerar password automaticamente
                    </button>
                    <p className="text-xs text-[var(--muted-foreground)] mt-2">
                      Gera uma password segura de 16 caracteres
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-card-foreground mb-2"
                      >
                        Nova password{" "}
                        <span className="text-[var(--destructive)]">
                          *
                        </span>
                      </label>
                      <div className="relative">
                        <LockClosed24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(event) =>
                            setPassword(event.target.value)
                          }
                          required={resetPassword}
                          autoComplete="new-password"
                          placeholder="Digite a nova password"
                          className="w-full h-10 pl-10 pr-10 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                          style={{ borderRadius: "var(--radius)" }}
                          disabled={isReadOnly}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword((prev) => !prev)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                          disabled={isReadOnly}
                        >
                          {showPassword ? (
                            <EyeOff20Regular />
                          ) : (
                            <Eye20Regular />
                          )}
                        </button>
                      </div>
                      {passwordStrength && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[var(--accent)] rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all duration-300"
                              style={{
                                width: `${(passwordStrength.score / 5) * 100}%`,
                                backgroundColor:
                                  passwordStrength.color,
                              }}
                            />
                          </div>
                          <span
                            className="text-xs"
                            style={{
                              color: passwordStrength.color,
                            }}
                          >
                            {passwordStrength.label}
                          </span>
                        </div>
                      )}
                      {resetPassword && password.length > 0 && (
                        <div className="mt-2 space-y-1 text-xs">
                          <div
                            className={`flex items-center gap-1.5 ${
                              passwordRequirements.minLength
                                ? "text-[var(--success)]"
                                : "text-[var(--muted-foreground)]"
                            }`}
                          >
                            {passwordRequirements.minLength ? (
                              <Checkmark16Filled />
                            ) : (
                              <Dismiss20Regular />
                            )}
                            <span>6 ou mais caracteres</span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 ${
                              passwordRequirements.hasSpecial
                                ? "text-[var(--success)]"
                                : "text-[var(--muted-foreground)]"
                            }`}
                          >
                            {passwordRequirements.hasSpecial ? (
                              <Checkmark16Filled />
                            ) : (
                              <Dismiss20Regular />
                            )}
                            <span>1 caracter especial</span>
                          </div>
                        </div>
                      )}
                      {resetPassword && !passwordMeetsPolicy && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-[var(--destructive)]">
                          <Warning20Regular className="mt-0.5" />
                          <span>
                            A password só será gravada se cumprir os requisitos mínimos acima.
                            Caso contrário, a alteração não será aplicada.
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-card-foreground mb-2"
                      >
                        Confirmar password{" "}
                        <span className="text-[var(--destructive)]">
                          *
                        </span>
                      </label>
                      <div className="relative">
                        <LockClosed24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                        <input
                          id="confirmPassword"
                          type={
                            showConfirmPassword ? "text" : "password"
                          }
                          value={confirmPassword}
                          onChange={(event) =>
                            setConfirmPassword(event.target.value)
                          }
                          required={resetPassword}
                          autoComplete="new-password"
                          placeholder="Confirme a nova password"
                          className="w-full h-10 pl-10 pr-10 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                          style={{ borderRadius: "var(--radius)" }}
                          disabled={isReadOnly}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                          disabled={isReadOnly}
                        >
                          {showConfirmPassword ? (
                            <EyeOff20Regular />
                          ) : (
                            <Eye20Regular />
                          )}
                        </button>
                      </div>
                      {confirmPassword && (
                        <div className="mt-2">
                          {passwordsMatch ? (
                            <div className="flex items-center gap-1.5 text-xs text-[var(--success)]">
                              <Checkmark16Filled />
                              <span>As passwords coincidem</span>
                            </div>
                          ) : (
                            <div className="text-xs text-[var(--destructive)]">
                              As passwords não coincidem
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setResetPassword(false);
                        setPassword("");
                        setConfirmPassword("");
                        setShowPassword(false);
                        setShowConfirmPassword(false);
                      }}
                      className="text-sm text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                      disabled={isReadOnly}
                    >
                      Cancelar redefinição de password
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              className="bg-card p-6 mb-6 border-2 border-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-[var(--destructive)] mb-4 flex items-center gap-2">
                <Warning20Regular />
                Zona de perigo
              </h2>

              {hasAssociatedData ? (
                <div>
                  <div
                    className="p-4 mb-4 bg-[var(--destructive)]/10 border border-[var(--destructive)]/20"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    <p className="text-sm text-[var(--destructive)] mb-2 flex items-start gap-2">
                      <Warning20Regular className="flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>
                          Não é possível eliminar este utilizador.
                        </strong>
                        <br />
                        Este utilizador tem informação associada
                        (timesheets, obras, documentos, etc.) que
                        não pode ser removida.
                      </span>
                    </p>
                  </div>
                  <p className="text-sm text-[var(--muted-foreground)] mb-4">
                    Para impedir o acesso ao sistema, altere o estado
                    para <strong>Inativo</strong>. O utilizador ficará
                    desativado mas toda a informação associada será
                    preservada.
                  </p>
                  {estado !== "Inativo" && (
                    <button
                      type="button"
                      onClick={handleMarkInactive}
                      disabled={isSaving || isReadOnly}
                      className="h-10 px-4 bg-[var(--destructive)] text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      <EyeOff20Regular />
                      Marcar como Inativo
                    </button>
                  )}
                  {estado === "Inativo" && (
                    <div
                      className="p-3 bg-[var(--accent)] border border-[var(--border)] flex items-center gap-2"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <EyeOff20Regular className="text-[var(--muted-foreground)]" />
                      <span className="text-sm text-[var(--muted-foreground)]">
                        Este utilizador já está inativo e não pode
                        aceder ao sistema.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-[var(--muted-foreground)] mb-4">
                    Este utilizador não tem informação associada e
                    pode ser eliminado. Esta ação é{" "}
                    <strong>irreversível</strong> e todos os dados do
                    utilizador serão permanentemente removidos do
                    sistema.
                  </p>

                  {!showDeleteConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="h-10 px-4 bg-[var(--destructive)] text-white hover:opacity-90 transition-all flex items-center gap-2"
                      style={{ borderRadius: "var(--radius-button)" }}
                      disabled={isReadOnly}
                    >
                      <Delete20Regular />
                      Eliminar utilizador
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div
                        className="p-4 bg-[var(--destructive)]/10 border border-[var(--destructive)]/20"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <p className="text-sm text-[var(--destructive)]">
                          <strong>
                            Tem a certeza que deseja eliminar este
                            utilizador?
                          </strong>
                          <br />
                          Esta ação não pode ser revertida.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={isSaving || isReadOnly}
                          className="h-10 px-4 bg-[var(--destructive)] text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          style={{ borderRadius: "var(--radius-button)" }}
                        >
                          <Delete20Regular />
                          Sim, eliminar
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isSaving || isReadOnly}
                          className="h-10 px-4 bg-[var(--secondary)] border border-[var(--border)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ borderRadius: "var(--radius-button)" }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
